import { KyInstance } from 'ky'
import { z } from 'zod'

import { AssetLocationHistory, AssetLocationHistorySchema } from './useAssetLocationHistory'

// The trail comes out of cold storage, which the docs put at "typically a few seconds". A minute
// of polling leaves plenty of headroom before the query is treated as stuck.
export const TRAIL_POLL_INTERVAL_MS = 1_000
export const TRAIL_MAX_POLL_ATTEMPTS = 60

const PendingTrailSchema = z.object({ url: z.string().min(1) })

export class TripLocationTrailTimeoutError extends Error {
  constructor(tripId: string, attempts: number) {
    super(`Location trail for trip ${tripId} was not ready after ${attempts} polls`)
    this.name = 'TripLocationTrailTimeoutError'
  }
}

/**
 * The submit endpoint hands back an absolute path ("/v2/api/..."), but ky refuses a leading slash
 * on input when a prefixUrl is set, so it is made relative to the telemetry host.
 */
export const toRelativeTelemetryPath = (url: string) => url.replace(/^\/+/, '')

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/** The trip to fetch a trail for, with its scheduled start and end. */
export type TripLocationTrailQuery = {
  tripId: string
  startInstant: Date
  endInstant: Date
}

export type FetchTripLocationTrailArgs = TripLocationTrailQuery & {
  client: KyInstance
  fleetName: string
  maxResolution: string
  pollIntervalMs?: number
  maxPollAttempts?: number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Fetches a trip's recorded location trail via the two-step submit-then-poll flow: POST the query
 * for a one-time result URL, then GET that URL until it stops answering 202.
 *
 * Unlike location-replay, the trail is served from durable history, so it reaches back past the
 * last few days a live replay can see. It only covers the trip from its first location entry on —
 * a trip that never reached its first stop resolves to an empty trail, not an error.
 */
export const fetchTripLocationTrail = async ({
  client,
  fleetName,
  tripId,
  startInstant,
  endInstant,
  maxResolution,
  pollIntervalMs = TRAIL_POLL_INTERVAL_MS,
  maxPollAttempts = TRAIL_MAX_POLL_ATTEMPTS,
  sleep = wait,
}: FetchTripLocationTrailArgs): Promise<AssetLocationHistory> => {
  const submitted = await client
    .post(`v2/api/fleets/${encodeURIComponent(fleetName)}/trips/location-trail`, {
      json: {
        trip_id: tripId,
        max_resolution: maxResolution,
        // Not read by the endpoint yet — it bounds the trail by the trip it registered. Sent so
        // the backend can bound or validate by them later without a client change.
        start_instant: startInstant.toISOString(),
        end_instant: endInstant.toISOString(),
      },
    })
    .json()

  const resultPath = toRelativeTelemetryPath(PendingTrailSchema.parse(submitted).url)

  for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
    const response = await client.get(resultPath)

    if (response.status !== 202) {
      return AssetLocationHistorySchema.parse(await response.json())
    }

    await sleep(pollIntervalMs)
  }

  throw new TripLocationTrailTimeoutError(tripId, maxPollAttempts)
}
